/**
 * Repositorio de suscripciones: misma frontera de migracion que el de dispositivos.
 *
 * Nota importante sobre pagos: el flujo real NUNCA debe pasar datos de tarjeta
 * por este codigo ni por nuestro backend. El patron correcto es que el
 * proveedor (Stripe, Mercado Pago) devuelva una URL de checkout alojada por el,
 * y que nosotros solo redirijamos. Por eso `subscribe` devuelve, en el modo
 * real, un `checkoutUrl` en lugar de confirmar la suscripcion: quien la
 * confirma es el webhook del proveedor contra nuestro backend.
 */

import { mockApi, ApiError, ERROR_CODE } from '../mock/mockBackend';
import {
  normalizeSubscription, PLAN_CATALOG, PLAN_ID, SUBSCRIPTION_STATUS,
} from '../../domain/subscription';

import { isDemoSession } from '../demoSession';

/** Fuente de datos decidida por sesión: demo -> simulado, real -> API. */
const usarSimulacion = () => isDemoSession();

/**
 * NADA de esto existe todavía en el backend.
 *
 * API Gateway no expone `/plans` ni `/account/subscription`: las suscripciones
 * están diseñadas (PLAN-BACKEND-SUSCRIPCIONES.md) pero no construidas. Llamar
 * igualmente a esas rutas no daba un error claro sino un fallo de CORS —API
 * Gateway responde 403 a una ruta desconocida y el preflight nunca llega a
 * devolver 200—, que llenaba la consola de una cuenta real con errores de red
 * que no describían el problema.
 *
 * Se falla en seco y con un mensaje que sí lo describe, igual que hace
 * `deviceRepository.link()`. Cuando el backend exista, este bloque se sustituye
 * por las llamadas reales y no hay que tocar nada más.
 */
const SIN_BACKEND = 'Las suscripciones aún no están disponibles en tu cuenta.';
const noImplementado = () => new ApiError(ERROR_CODE.NETWORK, SIN_BACKEND);

function traducirError(err) {
  if (err instanceof ApiError) return err;
  const codigo = err?.response?.data?.code;
  if (codigo && Object.values(ERROR_CODE).includes(codigo)) {
    return new ApiError(codigo, err.response.data.message || err.message);
  }
  if (err?.response?.status === 402) {
    return new ApiError(ERROR_CODE.PAYMENT_DECLINED, 'El pago no pudo procesarse');
  }
  return new ApiError(ERROR_CODE.NETWORK, err?.message || 'Error de conexión');
}

export const subscriptionRepository = {
  /**
   * Catalogo de planes.
   *
   * TEMPORAL: hoy sale de la constante del dominio. El precio y los limites
   * deben venir del servidor en cuanto exista: si el cliente decide el precio,
   * cualquiera puede cambiarlo desde las herramientas del navegador.
   *
   * PUNTO DE MIGRACION: `GET /plans`.
   */
  async listPlans() {
    // El mismo catalogo para todos. Antes se devolvia vacio en cuentas reales
    // para no ensenar precios que nadie podia contratar; ahora los planes estan
    // definidos y se muestran como informacion, con el alta deshabilitada y el
    // descargo al pie de la pantalla diciendo que no son una oferta.
    return PLAN_CATALOG;
  },

  /** PUNTO DE MIGRACION: `GET /account/subscription`. */
  async get() {
    try {
      if (!usarSimulacion()) {
        // Sin backend de suscripciones, toda cuenta real esta en el plan base.
        // Es la verdad hoy, y no requiere llamada.
        return normalizeSubscription({ plan_id: PLAN_ID.FREE, status: SUBSCRIPTION_STATUS.ACTIVE });
      }
      return normalizeSubscription(await mockApi.getSubscription());
    } catch (err) {
      throw traducirError(err);
    }
  },

  /**
   * Inicia el alta de un plan.
   *
   * SIMULADO: en mocks confirma la suscripcion directamente, cosa que el
   * sistema real no hara nunca.
   *
   * PUNTO DE MIGRACION: `POST /account/subscription` devuelve `{checkoutUrl}`.
   * El cliente redirige alli; la suscripcion solo pasa a `active` cuando llega
   * el webhook `checkout.session.completed` firmado por el proveedor.
   *
   * @param {object} opciones - `simulateFailure` permite probar el estado de
   *   error de la interfaz sin depender de que el mock falle por azar.
   */
  async subscribe(planId, opciones = {}) {
    try {
      if (usarSimulacion()) {
        const crudo = await mockApi.subscribe(planId, opciones);
        return { subscription: normalizeSubscription(crudo), checkoutUrl: null };
      }
      throw noImplementado();
    } catch (err) {
      throw traducirError(err);
    }
  },

  /**
   * Cancela al final del periodo ya pagado, no de inmediato.
   * PUNTO DE MIGRACION: `DELETE /account/subscription`.
   */
  async cancel() {
    try {
      if (!usarSimulacion()) throw noImplementado();
      return normalizeSubscription(await mockApi.cancelSubscription());
    } catch (err) {
      throw traducirError(err);
    }
  },

  /** Deshace una cancelacion antes de que venza el periodo. */
  async resume() {
    try {
      if (!usarSimulacion()) throw noImplementado();
      return normalizeSubscription(await mockApi.resumeSubscription());
    } catch (err) {
      throw traducirError(err);
    }
  },
};

export default subscriptionRepository;
